import { useQuery } from '@tanstack/react-query';
import { DepartmentSchema, ReferenceMasterSchema } from '@taqon/contracts';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';

/** The scope columns of a role assignment (ERD §8.1). */
export interface AssignmentScope {
  departmentId: string | null;
  costCenterId: string | null;
  projectId: string | null;
  locationId: string | null;
  clientId: string | null;
}

/**
 * The organizational scope of a role assignment (SDD §11.4 step 2; FRD-USR-007), named from the
 * masters the session may read. An assignment without scope applies to every record. A project is
 * named on its own by `ScopeText`, which reads it through the projects API.
 */
export function useScopeNames() {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const departments = useQuery({
    queryKey: ['departments', 'all'],
    enabled: can('organization.department:view'),
    queryFn: () =>
      getPage('/api/v1/organization/departments?pageSize=200&sort=code', DepartmentSchema),
  });
  const costCentres = useQuery({
    queryKey: ['masters-all', 'cost-centers'],
    enabled: can('organization.cost_center:view'),
    queryFn: () =>
      getPage('/api/v1/organization/cost-centers?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const locations = useQuery({
    queryKey: ['masters-all', 'locations'],
    enabled: can('organization.location:view'),
    queryFn: () =>
      getPage('/api/v1/organization/locations?pageSize=200&sort=code', ReferenceMasterSchema),
  });
  const name = (
    rows: readonly { id: string; code: string; name: string }[] | undefined,
    id: string,
  ) => {
    const found = rows?.find((row) => row.id === id);
    return found === undefined ? 'not visible' : `${found.code} — ${found.name}`;
  };
  return {
    departments: departments.data?.items ?? [],
    costCentres: costCentres.data?.items ?? [],
    locations: locations.data?.items ?? [],
    can: {
      departments: can('organization.department:view'),
      costCentres: can('organization.cost_center:view'),
      locations: can('organization.location:view'),
      projects: can('projects.project:view'),
      clients: can('crm.client:view'),
    },
    /** The department, cost centre and location parts of a scope, as text. */
    organizationParts(scope: AssignmentScope): string[] {
      return [
        scope.departmentId === null
          ? null
          : `Department ${name(departments.data?.items, scope.departmentId)}`,
        scope.costCenterId === null
          ? null
          : `Cost centre ${name(costCentres.data?.items, scope.costCenterId)}`,
        scope.locationId === null
          ? null
          : `Location ${name(locations.data?.items, scope.locationId)}`,
      ].filter((part): part is string => part !== null);
    },
  };
}
